import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { thinkingFrameworkService, ThinkingFrameworkSession, SWOTData } from '../../services/thinkingFrameworkService';
import PageHeader from '../common/PageHeader';
import FrameworkGuide from '../common/FrameworkGuide';
import './SWOTAnalysis.css';

interface SWOTAnalysisProps {
  onToggleSidebar: () => void;
}

const SWOTGuideContent = (
  <div>
    <h3>What is SWOT Analysis?</h3>
    <p>SWOT Analysis is a strategic planning technique used to help a person or organization identify strengths, weaknesses, opportunities, and threats related to business competition or project planning.</p>
    
    <h3>How to use it</h3>
    <ul>
      <li><strong>Strengths (Internal, Positive):</strong> Characteristics of the business or project that give it an advantage over others. What do you do well? What unique resources do you have?</li>
      <li><strong>Weaknesses (Internal, Negative):</strong> Characteristics that place the business or project at a disadvantage relative to others. What could you improve? What should you avoid?</li>
      <li><strong>Opportunities (External, Positive):</strong> Elements in the environment that the business or project could exploit to its advantage. What trends can you take advantage of?</li>
      <li><strong>Threats (External, Negative):</strong> Elements in the environment that could cause trouble for the business or project. What obstacles do you face? What is your competition doing?</li>
    </ul>

    <h3>Tips</h3>
    <ul>
      <li>Be realistic about the strengths and weaknesses.</li>
      <li>Distinguish between where you are today (Strengths/Weaknesses), and where you could be in the future (Opportunities/Threats).</li>
      <li>Always be specific. Avoid grey areas.</li>
      <li>Keep your SWOT short and simple. Avoid complexity and over-analysis.</li>
    </ul>
  </div>
);

const SWOTAnalysis: React.FC<SWOTAnalysisProps> = ({ onToggleSidebar }) => {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState<ThinkingFrameworkSession | null>(null);
  const [title, setTitle] = useState('');
  const [swotData, setSwotData] = useState<SWOTData>({
    strengths: [],
    weaknesses: [],
    opportunities: [],
    threats: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoSaveTimer, setAutoSaveTimer] = useState<NodeJS.Timeout | null>(null);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    if (user) {
      if (sessionId) {
        loadSession();
      } else {
        // New session - initialize empty
        setTitle('');
        setSwotData({
          strengths: [],
          weaknesses: [],
          opportunities: [],
          threats: []
        });
        setLoading(false);
        // Show guide for new sessions
        setShowGuide(true);
      }
    }
  }, [user, sessionId]);

  const loadSession = async () => {
    if (!sessionId || !user) return;

    try {
      setLoading(true);
      setError(null);
      const loadedSession = await thinkingFrameworkService.getSession(sessionId);
      setSession(loadedSession);
      setTitle(loadedSession.title);
      setSwotData(loadedSession.framework_data as SWOTData);
    } catch (err) {
      console.error('Error loading SWOT session:', err);
      setError('Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const saveSession = useCallback(async (dataToSave: { title?: string; framework_data?: SWOTData }) => {
    if (!user) return;

    try {
      setSaving(true);
      setError(null);

      if (sessionId && session) {
        // Update existing session
        const updated = await thinkingFrameworkService.updateSession(sessionId, dataToSave);
        setSession(updated);
      } else {
        // Create new session
        const newSession = await thinkingFrameworkService.createSession({
          framework_type: 'swot',
          title: dataToSave.title || 'Untitled SWOT Analysis',
          framework_data: dataToSave.framework_data || swotData
        });
        setSession(newSession);
        navigate(`/thinking-frameworks/swot/${newSession.session_id}`, { replace: true });
      }
    } catch (err) {
      console.error('Error saving SWOT session:', err);
      setError('Failed to save session');
    } finally {
      setSaving(false);
    }
  }, [user, sessionId, session, swotData, navigate]);

  // Debounced auto-save
  useEffect(() => {
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }

    const hasContent = title || swotData.strengths.length > 0 || swotData.weaknesses.length > 0 || 
        swotData.opportunities.length > 0 || swotData.threats.length > 0;

    if (hasContent) {
      const timer = setTimeout(() => {
        saveSession({ title, framework_data: swotData });
      }, 2000); // Auto-save after 2 seconds of inactivity
      setAutoSaveTimer(timer);
    }

    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, swotData]);

  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
  };

  const handleQuadrantChange = (quadrant: keyof SWOTData, items: string[]) => {
    setSwotData(prev => ({
      ...prev,
      [quadrant]: items
    }));
  };

  const handleDelete = async () => {
    if (!sessionId) return;
    if (!window.confirm('Are you sure you want to delete this SWOT analysis?')) return;

    try {
      setError(null);
      await thinkingFrameworkService.deleteSession(sessionId);
      navigate('/thinking-frameworks');
    } catch (err) {
      console.error('Error deleting session:', err);
      setError('Failed to delete session');
    }
  };

  const handleBack = () => {
    navigate('/thinking-frameworks');
  };

  if (loading) {
    return (
      <div className="swot-analysis">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="swot-analysis-header-title">/swot</h1>}
        />
        <div className="swot-analysis-content">
          <div className="swot-analysis-loading">Loading SWOT analysis...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="swot-analysis">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="swot-analysis-header-title">/swot</h1>}
        rightActions={
          <div className="swot-analysis-actions">
            {saving && <span className="swot-analysis-saving">Saving...</span>}
            <button className="framework-guide-btn" onClick={() => setShowGuide(true)}>
              💡 Guide
            </button>
            {sessionId && (
              <button className="swot-analysis-delete-btn" onClick={handleDelete}>
                Delete
              </button>
            )}
            <button className="swot-analysis-back-btn" onClick={handleBack}>
              ← Back
            </button>
          </div>
        }
      />
      
      <div className="swot-analysis-content">
        {error && (
          <div className="swot-analysis-error">{error}</div>
        )}

        <div className="swot-analysis-title-input">
          <input
            type="text"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="Enter SWOT analysis title..."
            className="swot-analysis-title-field"
          />
        </div>

        <div className="swot-analysis-grid">
          <SWOTQuadrant
            label="Strengths"
            items={swotData.strengths}
            onChange={(items) => handleQuadrantChange('strengths', items)}
          />
          <SWOTQuadrant
            label="Weaknesses"
            items={swotData.weaknesses}
            onChange={(items) => handleQuadrantChange('weaknesses', items)}
          />
          <SWOTQuadrant
            label="Opportunities"
            items={swotData.opportunities}
            onChange={(items) => handleQuadrantChange('opportunities', items)}
          />
          <SWOTQuadrant
            label="Threats"
            items={swotData.threats}
            onChange={(items) => handleQuadrantChange('threats', items)}
          />
        </div>
      </div>

      <FrameworkGuide
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
        title="SWOT Analysis"
        content={SWOTGuideContent}
      />
    </div>
  );
};

interface SWOTQuadrantProps {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
}

const SWOTQuadrant: React.FC<SWOTQuadrantProps> = ({ label, items, onChange }) => {
  const [text, setText] = useState(items.join('\n'));

  useEffect(() => {
    setText(items.join('\n'));
  }, [items]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setText(newText);
    const newItems = newText.split('\n').filter(line => line.trim() !== '');
    onChange(newItems);
  };

  return (
    <div className="swot-quadrant">
      <h3 className="swot-quadrant-label">{label}</h3>
      <textarea
        className="swot-quadrant-textarea"
        value={text}
        onChange={handleChange}
        placeholder={`Enter ${label.toLowerCase()}...`}
        rows={8}
      />
      <div className="swot-quadrant-count">{items.length} items</div>
    </div>
  );
};

export default SWOTAnalysis;
